// Hiệu ứng donate theo mốc: hàm thuần dùng chung cho overlay OBS và trang cài đặt.

export interface AlertTier {
  id?: string;
  minAmount: number;
  sound: string; // "lib:sound_3.mp3"
  image: string; // "lib:img_2.gif"
  durationSec: number;
  textColor: string | null;
  readAloud: boolean;
  template: string | null;
  active?: boolean;
  soundUrl?: string | null;
  imageUrl?: string | null;
  /** Kiểu thông báo: "normal" | "highlight" | "special" (xem utils/alert-kind). Máy chủ cũ không trả thì là Thường. */
  kind?: string | null;
  /** Ghi đè bộ cài sẵn của kiểu; null = theo kiểu. */
  introEnabled?: boolean | null;
  medalEnabled?: boolean | null;
  effectEnabled?: boolean | null;
  voice?: string | null;
  /** File của designer (chỉ đọc, chưa có trên giao diện). */
  introUrl?: string | null;
  medalUrl?: string | null;
}

export const DEFAULT_ALERT_MS = 6000;
export const MAX_ALERT_MS = 60000;
export const MAX_QUEUE = 50;
export const DEFAULT_TEMPLATE = '{ten} đã donate {so_tien}';

export const formatCa = (v: number) => `${Number(v || 0).toLocaleString('vi-VN')} Ca`;

/** Mốc có minAmount lớn nhất mà không vượt số Ca người xem gửi; không có thì null (dùng alert cũ). */
export const selectTier = (tiers: AlertTier[], amount: number): AlertTier | null => {
  let best: AlertTier | null = null;
  (tiers || []).forEach((tier) => {
    if (tier.active === false || tier.minAmount > amount) return;
    if (!best || tier.minAmount > best.minAmount) best = tier;
  });
  return best;
};

/** Thay biến MỘT lượt (tên người donate có "{so_tien}" cũng không bị thay lại). Kết quả là chữ thường, không HTML. */
export const renderTemplate = (
  template: string | null,
  vars: { ten: string; soTien: number; loiNhan?: string | null }
): string => {
  const map: Record<string, string> = {
    ten: vars.ten ?? '',
    so_tien: formatCa(vars.soTien),
    loi_nhan: vars.loiNhan ?? '',
  };
  return (template || DEFAULT_TEMPLATE)
    .replace(/\{(ten|so_tien|loi_nhan)\}/g, (_m, key: string) => map[key])
    .trim();
};

const LIB_FILE = /^lib:([a-z0-9_]+\.(mp3|gif))$/i;
export const libFile = (key: string): string | null => {
  const m = LIB_FILE.exec(key || '');
  return m ? m[1] : null;
};

const ASSET_HOSTS = ['www.dropbox.com', 'dl.dropboxusercontent.com'];

/** Chỉ phát file ở máy chủ Dropbox của Cab (https). */
export const isAllowedAssetUrl = (url?: string | null): boolean => {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && ASSET_HOSTS.includes(u.hostname);
  } catch {
    return false;
  }
};

const assetSrc = (key: string, url: string | null | undefined, folder: 'sound' | 'gif'): string | null => {
  const lib = libFile(key);
  if (lib) return `/${folder}/${lib}`;
  if ((key || '').startsWith('up:') && isAllowedAssetUrl(url)) return url as string;
  return null;
};

export const tierSoundSrc = (t: AlertTier) => assetSrc(t.sound, t.soundUrl, 'sound');
export const tierImageSrc = (t: AlertTier) => assetSrc(t.image, t.imageUrl, 'gif');

// Lời nhắn giọng nói khi donate: overlay phát đoạn ghi âm sau mốc/tiếng đọc máy.

/** Chỉ nhận URL chuỗi thuộc host Dropbox cho phép; khác thì bỏ (không phát). */
export const safeVoiceUrl = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;
  return isAllowedAssetUrl(raw) ? raw : null;
};

export type AlertStage = 'tts' | 'voice';

/** Thứ tự các bước phát "tiếng" của một alert: đọc máy trước, ghi âm sau. */
export const alertStages = (o: { readAloud: boolean; hasVoice: boolean }): AlertStage[] => {
  const stages: AlertStage[] = [];
  if (o.readAloud) stages.push('tts');
  if (o.hasVoice) stages.push('voice');
  return stages;
};

/** Có ghi âm thì đọc máy chỉ đọc câu chính (text), không đọc kèm lời nhắn chữ (message). */
export const spokenText = (o: { text: string; message?: string; hasVoice: boolean }): string => {
  if (o.hasVoice) return o.text;
  return o.message ? `${o.text}. ${o.message}` : o.text;
};

/** Donate không khớp mốc nào thì tắt đúng giờ mặc định — TRỪ KHI có ghi âm, để ghi âm phát hết. */
export const shouldHardEnd = (o: { tierMatched: boolean; hasVoice: boolean }): boolean =>
  !o.tierMatched && !o.hasVoice;
